import { IPublication, IStory, IStoryContent } from '@opengov/stories-shared/types';
import { clamp } from 'lodash';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';

import { STORY_DEFAULT_PADDING_PIXELS } from '../../../stories/constants';
import { updateSpacingDefaults } from '../../../stories/utils/dom';
import { IExtendedStoryPrivilege, IModalMessages, IUpdateStoryProps } from '../../../types';

import { styles } from './StorySettings.styles';

const STORY_MINIMUM_PADDING_PIXELS = 0;
const STORY_MAXIMUM_PADDING_PIXELS = 1000;

function parsePaddingValue(stringValue: string): number | undefined {
  const floatValue = parseFloat(stringValue.replace(/[$¢€,]+/g, ''));

  if (isNaN(floatValue)) {
    return undefined;
  }

  return clamp(floatValue, STORY_MINIMUM_PADDING_PIXELS, STORY_MAXIMUM_PADDING_PIXELS);
}

export type IStorySettingsProps = {
  open: boolean;
  onClose(): void;
  isViewingStoryEditor: boolean;
  entitySubdomain: string;
  storyPaddingEnabled: boolean;
  isAdmin: boolean;
  isOpenGovAdmin: boolean;
  story: IStory;
  isTemplate: boolean;
  title: string;
  description: string;
  paddingTop?: number;
  paddingBottom?: number;
  messages: IModalMessages;
  storyContents: IStoryContent[];
  publication?: IPublication;
  userId: number;
  currentSubjectPublicationStoryClaim: IExtendedStoryPrivilege | null;
  storiesAccessControlEnabled: boolean;
  updateStory(props: Partial<IUpdateStoryProps>): void;
  updateAllStoryContentsDB(props: {
    isTemplate: boolean;
    story: IStory;
    storyContents: IStoryContent[];
    entitySubdomain: string;
  }): void;
  updatePublicationUpdatedAt(props: { publicationId: string; userId: number }): Promise<void>;
};

export const StorySettings: React.FC<IStorySettingsProps> = ({
  open,
  onClose,
  isViewingStoryEditor,
  entitySubdomain,
  storyPaddingEnabled,
  isAdmin,
  story,
  isTemplate,
  title: initialTitle,
  description: initialDescription,
  paddingTop: initialPaddingTop,
  paddingBottom: initialPaddingBottom,
  messages,
  storyContents,
  publication,
  userId,
  currentSubjectPublicationStoryClaim,
  storiesAccessControlEnabled,
  updateStory,
  updateAllStoryContentsDB,
  updatePublicationUpdatedAt,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription || '');
  const [paddingTop, setPaddingTop] = useState<number | undefined>(
    initialPaddingTop ?? STORY_DEFAULT_PADDING_PIXELS
  );
  const [paddingBottom, setPaddingBottom] = useState<number | undefined>(
    initialPaddingBottom ?? STORY_DEFAULT_PADDING_PIXELS
  );

  if (!open) return null;

  const titleMessage = isTemplate
    ? `${messages.template} ${messages.settings}`
    : `${messages.story} ${messages.settings}`;

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const updatedStory: IStory = {
      ...story,
      title,
      description,
      paddingBottom,
      paddingTop,
    };

    onClose();
    void updateStory({
      story: updatedStory,
      isTemplate,
      isAdmin,
      entitySubdomain,
      currentSubjectPublicationStoryClaim,
      storiesAccessControlEnabled,
    });
    void updateAllStoryContentsDB({
      isTemplate,
      storyContents: storyContents.map((storyContent) =>
        updateSpacingDefaults(storyContent, { paddingTop, paddingBottom })
      ),
      story: updatedStory,
      entitySubdomain,
    });
    if (publication?.id) {
      void updatePublicationUpdatedAt({ publicationId: publication.id, userId });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: styles.dialogPaper }}
      aria-labelledby="story-settings-dialog-title"
    >
      <DialogTitle id="story-settings-dialog-title" sx={styles.dialogTitle}>
        {titleMessage}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.formItem}>
          <Box sx={styles.labelRow}>
            <Typography id="story-settings-title-label" variant="body2" sx={styles.formLabel}>
              {messages.title}
            </Typography>
            <Typography variant="caption" sx={styles.characterCounter}>
              {title.length}/60
            </Typography>
          </Box>
          <TextField
            size="small"
            sx={styles.textField}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{
              maxLength: 60,
              className: 'no-format',
              'data-test': 'story-title-field',
              'aria-labelledby': 'story-settings-title-label',
            }}
          />
        </Box>

        <Box sx={styles.formItem}>
          <Box sx={styles.labelRow}>
            <Typography id="story-settings-description-label" variant="body2" sx={styles.formLabel}>
              {messages.description}
            </Typography>
            <Typography variant="caption" sx={styles.characterCounter}>
              {description.length}/150
            </Typography>
          </Box>
          <TextField
            size="small"
            multiline
            rows={2}
            sx={styles.descriptionTextField}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{
              maxLength: 150,
              className: 'no-format',
              'data-test': 'story-description-field',
              'aria-labelledby': 'story-settings-description-label',
            }}
          />
        </Box>

        {storyPaddingEnabled && isViewingStoryEditor && (
          <Box sx={styles.spacingSection}>
            <Typography variant="h6">{messages.adjustSpacing}</Typography>
            <Typography variant="body2" sx={styles.spacingSubtitle}>
              {messages.customizeSpacing}
            </Typography>
            <Box sx={styles.spacingRow}>
              <Box data-test="story-settings-top-padding">
                <Typography variant="body2" sx={styles.formLabel}>
                  {messages.topPadding}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  sx={styles.numberTextField}
                  placeholder={STORY_DEFAULT_PADDING_PIXELS.toString()}
                  value={paddingTop ?? ''}
                  onChange={(e) => setPaddingTop(parsePaddingValue(e.target.value))}
                  inputProps={{
                    'data-test': 'story-settings-top-padding-input',
                    min: STORY_MINIMUM_PADDING_PIXELS,
                    max: STORY_MAXIMUM_PADDING_PIXELS,
                    className: 'no-format',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{messages.unitPixels}</InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box data-test="story-settings-bottom-padding">
                <Typography variant="body2" sx={styles.formLabel}>
                  {messages.bottomPadding}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  sx={styles.numberTextField}
                  placeholder={STORY_DEFAULT_PADDING_PIXELS.toString()}
                  value={paddingBottom ?? ''}
                  onChange={(e) => setPaddingBottom(parsePaddingValue(e.target.value))}
                  inputProps={{
                    'data-test': 'story-settings-bottom-padding-input',
                    min: STORY_MINIMUM_PADDING_PIXELS,
                    max: STORY_MAXIMUM_PADDING_PIXELS,
                    className: 'no-format',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{messages.unitPixels}</InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button variant="outlined" color="secondary" size="small" onClick={onClose}>
          {messages.cancel}
        </Button>
        {title ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSubmit}
            data-test="settings-save"
          >
            {messages.save}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled
            data-test="settings-save-disabled"
          >
            {messages.save}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
